import { Link } from 'react-router-dom';
import { createStyles, Kbd, Menu, Navbar, Group, ActionIcon, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowBigRight, IconBuildingBank, IconShoppingCart, IconAffiliate, IconHome, IconUsers, IconLicense, IconSettings, IconAddressBook, IconBrandLinkedin } from '@tabler/icons';
import { useTranslation } from 'react-i18next';

const useStyles = createStyles((theme) => ({
  navBar: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[8] : theme.colors.gray[2],
  },
  subLink: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[8] : theme.colors.gray[2],
    color: theme.colorScheme === 'dark' ? theme.colors.blue[2] : theme.colors.blue[6],
    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.blue[8] : theme.colors.blue[0],
    },
    zIndex: 1000,
  }
}));

const NavbarContainer = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const menus = [
    {
      label: t('nav.cvtheque'),
      icon: <IconUsers size={16} />,
      links: [
        { url: "/cvtheque/insert", label: t('nav.cvtheque_insert'), shortcut: () => { return (<><Kbd>⌘</Kbd>+<Kbd>I</Kbd></>) }, },
        { url: "/cvtheque/search", label: t('nav.cvtheque_search'), shortcut: () => { return (<><Kbd>⌘</Kbd>+<Kbd>R</Kbd></>) }, },
        { url: "/cvtheque/list", label: t('nav.cvtheque_stock'), shortcut: null, },
        { url: "/cvtheque/dashboard/in_progress", label: t('nav.cvtheque_dashboardInProgress'), shortcut: null, },
        { url: "/cvtheque/dashboard/main", label: t('nav.cvtheque_dashboardMain'), shortcut: null, },
        { url: "/cvtheque/audit/blocked", label: t('nav.cvtheque_blocked'), shortcut: null, },
        { url: "/cvtheque/audit/duplicates", label: t('nav.cvtheque_duplicates'), shortcut: null, },
        { url: "/cvtheque/audit/metadata", label: t('nav.cvtheque_metadata'), shortcut: null, }
      ]
    },{
      label: t('nav.annuaire'),
      icon: <IconAddressBook size={16} />,
      links: [
        { url: "/annuaire/add/message", label: t('nav.annuaire_addEvent'), shortcut: () => { return (<><Kbd>⌘</Kbd>+<Kbd>E</Kbd></>) }, },
        { url: "/annuaire/clients/list", label: t('nav.annuaire_clients'), shortcut: null, },
        { url: "/annuaire/prospects/list", label: t('nav.annuaire_prospects'), shortcut: null, },
        { url: "/annuaire/references/list", label: t('nav.annuaire_references'), shortcut: null, },
        { url: "/annuaire/mailing", label: t('nav.annuaire_mailing'), shortcut: null, },
        { url: "/annuaire/dashboard", label: t('nav.annuaire_prospection'), shortcut: null, }
      ]
    },{
      label: t('nav.needs'),
      icon: <IconShoppingCart size={16} />,
      links: [
        { url: "/need/add", label: t('nav.needs_add'), shortcut: () => { return (<><Kbd>⌘</Kbd>+<Kbd>B</Kbd></>) }, },
        { url: "/need/list/open", label: t('nav.needs_listOpen'), shortcut: null, },
        { url: "/need/list/closed", label: t('nav.needs_listClosed'), shortcut: null, },
        { url: "/need/dashboard/main", label: t('nav.needs_dashboard'), shortcut: null, }
      ]
    },{
      label: t('nav.hunts'),
      icon: <IconBrandLinkedin size={16} />,
      links: [
        { url: "/hunt/add", label: t('nav.hunts_create'), shortcut: null, },
        { url: "/hunt/list", label: t('nav.hunts_list'), shortcut: null, }
      ]
    },{
      label: t('nav.missions'),
      icon: <IconLicense size={16} />,
      links: [
        { url: "/mission/overview/audit-activities", label: t('nav.missions_auditCRA'), shortcut: null, },
        { url: "/mission/overview/audit-factures", label: t('nav.missions_auditInvoices'), shortcut: null, },
        { url: "/mission/add", label: t('nav.missions_create'), shortcut: null, },
        { url: "/mission/list", label: t('nav.missions_list'), shortcut: null, },
        { url: "/mission/overview/not_paid", label: t('nav.missions_unpaid'), shortcut: null, },
        { url: "/mission/overview/removed-factures", label: t('nav.missions_removed'), shortcut: null, },
        { url: "/mission/search", label: t('nav.missions_searchInvoice'), shortcut: null, },
        { url: "/mission/overview/dashboard", label: t('nav.missions_financial'), shortcut: null, },
        { url: "/mission/overview/factures/taxes", label: t('nav.missions_vat'), shortcut: null, },
        { url: "/mission/overview/factures/ttc", label: t('nav.missions_ttc'), shortcut: null, }
      ]
    },{
      label: t('nav.bank'),
      icon: <IconBuildingBank size={16} />,
      links: [
        { url: "/bank/upload", label: t('nav.bank_upload'), shortcut: null, },
        { url: "/bank/lettering", label: t('nav.bank_reconciliation'), shortcut: null, },
        { url: "/bank/remove_lettering", label: t('nav.bank_removeLettering'), shortcut: null, }
      ]
    },{
      label: t('nav.config'),
      icon: <IconSettings size={16} />,
      links: [
        { url: "/configuration/account/add", label: t('nav.config_addUser'), shortcut: null, },
        { url: "/configuration/accounts", label: t('nav.config_manageUsers'), shortcut: null, },
        { url: "/configuration/permissions", label: t('nav.config_permissions'), shortcut: null, },
        { url: "/configuration/affectations", label: t('nav.config_affectations'), shortcut: null, },
        { url: "/configuration/export", label: t('nav.config_export'), shortcut: null, },
        { url: "/configuration/send_email", label: t('nav.config_sendEmail'), shortcut: null, }
      ]
    },{
      label: t('nav.team'),
      icon: <IconAffiliate size={16} />,
      links: [
        { url: "/team/orga", label: t('nav.team_orgChart'), shortcut: null, },
        { url: "/team/requests", label: t('nav.team_hrRequests'), shortcut: null, },
        { url: "/team/attendance", label: t('nav.team_absences'), shortcut: null, }
      ]
    }
  ];

  return (
    <>
      <Navbar width={{ base: 40 }} height='100vh' p={5} className={classes.navBar}>
        <Navbar.Section grow>
          <Stack>
            <ActionIcon variant="outline" color="blue" component={Link} to={'/home'}>
              <IconHome size={16}/>
            </ActionIcon>
            {menus.map((menu) => (
              <Menu key={menu.label} trigger="hover" position="right-start" exitTransitionDuration={0}>
                <Menu.Target>
                  <Tooltip label={menu.label} position="top" withArrow>
                    <ActionIcon variant="outline" color="blue">{menu.icon}</ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  {menu.links.map((link) => (
                    <Menu.Item key={link.url} component={Link} to={link.url} className={classes.subLink}>
                      <Group style={{width: '350px'}}>
                        <IconArrowBigRight size={12} />
                        <Text>{link.label}</Text>
                        {link.shortcut !== null &&
                          <Text color="dimmed">{link.shortcut()}</Text>
                        }
                      </Group>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ))}
          </Stack>
        </Navbar.Section>
      </Navbar>
    </>
  )
}

export { NavbarContainer }
